import React, { FC, useState } from "react";
import { Card, Form, Button, Input } from "antd";
import styled from "styled-components";
import { Store } from "antd/lib/form/interface";
import { useLoginMutation } from "../../queries/auto-generated/hooks";
import _ from "lodash";
import { ViewTypeUpdater } from ".";
import { useDispatch } from "react-redux";
import user from "../../ducks/user";
import { LockOutlined, MailOutlined } from "@ant-design/icons";
import useLogin from "../../hooks/useLogin";

const Login: FC<{ setType: ViewTypeUpdater }> = ({ setType }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loginMutation] = useLoginMutation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmittedAtLeastOnce, setHasSubmittedAtLeastOnce] = useState<
    boolean
  >(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const { handleLoginRedirect } = useLogin();

  const validateTrigger = hasSubmittedAtLeastOnce
    ? ["onChange", "onSubmit"]
    : "onSubmit";

  const handleButtonClick = () => {
    setIsSubmitting(true);
    setErrorMessages([]);
    setHasSubmittedAtLeastOnce(true);
    form.submit();
  };

  const handleFinish = (values: Store) => {
    const { email, password } = values;
    loginMutation({
      variables: {
        input: {
          identifier: email,
          password,
        },
      },
    })
      .then(({ data }) => {
        const jwt = data?.loginEnhanced.jwt;
        const userData = data?.loginEnhanced.user;

        if (!jwt || !userData) {
          throw new Error("Trouble logging in");
        }

        const { firstName, lastName, email, phoneNumber } = userData;
        const contactInfo = {
          firstName,
          lastName,
          email,
          phoneNumber,
        };

        dispatch(user.actions.login({ jwt, contactInfo }));
        dispatch(user.actions.updatingEditing(false));
        handleLoginRedirect();
      })
      .catch((e) => {
        console.error(e);
        const errorId = _.get(
          e,
          "graphQLErrors[0].extensions.exception.data.message[0].messages[0].id"
        );

        if (errorId === "Auth.form.error.invalid") {
          setErrorMessages(["Incorrect email or password"]);
        } else if (errorId === "Auth.form.error.confirmed") {
          setErrorMessages([
            "Your account email is not confirmed. Please check your email.",
          ]);
        } else {
          setErrorMessages(["Trouble communicating with the server : ("]);
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleFinishedFailed = () => {
    setIsSubmitting(false);
  };

  const ErrorMessages: FC = () => {
    if (!errorMessages || errorMessages.length === 0) {
      return null;
    }

    return (
      <StyledList>
        {errorMessages.map((message) => (
          <li>{message}</li>
        ))}
      </StyledList>
    );
  };

  const primaryAction = (
    <Button
      size="large"
      type="primary"
      shape="round"
      block
      onClick={handleButtonClick}
      loading={isSubmitting}
    >
      Login
    </Button>
  );

  return (
    <Card title="Login">
      <Form
        form={form}
        name="login"
        scrollToFirstError
        colon={false}
        hideRequiredMark
        onFinish={handleFinish}
        onFinishFailed={handleFinishedFailed}
        validateTrigger={validateTrigger}
      >
        <StyledItem
          name="email"
          rules={[
            {
              required: true,
              message: "Please input your email address",
              type: "email",
            },
          ]}
        >
          <Input
            placeholder="Email"
            prefix={<MailOutlined className={"input-prefix"} />}
          />
        </StyledItem>
        <StyledItem
          name="password"
          rules={[
            {
              required: true,
              message: "Please input your password",
            },
          ]}
        >
          <Input.Password
            placeholder="Password"
            prefix={<LockOutlined className={"input-prefix"} />}
          />
        </StyledItem>

        <ErrorMessages />
        {primaryAction}
        <AlternativeOptions>
          <Button type="text" shape="round" onClick={() => setType(undefined)}>
            Back
          </Button>
          <Button
            type="text"
            shape="round"
            onClick={() => setType("passwordReset")}
          >
            Lost password
          </Button>
        </AlternativeOptions>
      </Form>
    </Card>
  );
};

const StyledItem = styled(Form.Item)`
  display: block;
`;

const StyledList = styled.ul`
  list-style: none;
  color: #a61d24;
  padding-left: 0;
`;

const AlternativeOptions = styled.div`
  padding-top: 2em;
  text-align: center;
  display: block;
`;

export default Login;
